/**
 * Click-target geometry policy: what geometry a clicked feature contributes to the
 * stacked-vs-point navigation rules. Policy geometry is not raw GeoJSON.
 */
import * as Sentry from '@sentry/capacitor';
import { matchPath } from 'react-router-dom';
import { ENV } from 'shared/ENV';
import {
  DrawerEntityPoiType,
  FeatureWithEntityType,
  StandardPoiType,
} from 'features/mapping/mapping.types';
import { MapGeoJSONFeature } from 'shared/map-exports';
import { ClickGeometry } from 'features/mapping/mapClick/resolution';
import {
  RouterEntityDrawer,
  MODERATE_SUBMISSION_ROUTE_PATH,
  ROUTER_ENTITY_TO_ROUTE_PATH_MAP,
} from '../../../constants';
import { ExternalFeatureLayerMetadata } from '../components/layers/ExternalFeatureLayer';
import {
  CLICK_GEOMETRY_BY_DRAWER_ENTITY_POI,
  CLICK_GEOMETRY_BY_POI,
  CLICK_GEOMETRY_BY_ROUTER_ENTITY,
  ClickGeometryLayerIds,
  DeclaredClickGeometry,
} from './constants';

const GEOMETRIES: ClickGeometry[] = ['polygon', 'point', 'line'];

const isClickGeometry = (value: unknown): value is ClickGeometry =>
  GEOMETRIES.includes(value as ClickGeometry);

/**
 * Geometry assumed when a feature's own geometry cannot be determined.
 *
 * Must stay non-point: `hasPoint` is a set-level veto that suppresses stacking for the whole
 * click and defers every router, so a mis-defaulted point hides other targets. `polygon` over
 * `line` because polygons render below both lines and points, making it the lowest-precedence
 * assumption available for an unknown feature.
 */
const FALLBACK_GEOMETRY: ClickGeometry = 'polygon';

const geometryByLayerId = (
  layerIds: ClickGeometryLayerIds,
  feature: MapGeoJSONFeature,
): ClickGeometry => {
  const layerId = feature.layer?.id;
  if (layerId == null) return FALLBACK_GEOMETRY;
  return (
    GEOMETRIES.find((geometry) => layerIds[geometry]?.includes(layerId)) ??
    FALLBACK_GEOMETRY
  );
};

const warnUnresolvedLayerOrderSlot = (
  feature: MapGeoJSONFeature,
  slot: unknown,
): void => {
  const message =
    `[mapClick] Unrecognized layerOrderSlot ${JSON.stringify(slot)} on layer ` +
    `"${feature.layer?.id}". Treating clicks on it as '${FALLBACK_GEOMETRY}'.`;

  if (ENV.PROD) {
    Sentry.captureMessage('unresolved-layer-order-slot', {
      level: 'error',
      extra: {
        slot,
        layerId: feature.layer?.id,
        layerMetadata: feature.layer?.metadata,
      },
    });
    return;
  }
  console.warn(message);
};

/** External layers carry their geometry in the layer's `layerOrderSlot` metadata. */
const geometryFromLayerMetadata = (
  feature: MapGeoJSONFeature,
): ClickGeometry => {
  const metadata = feature.layer?.metadata as
    | ExternalFeatureLayerMetadata
    | undefined;
  const slot = metadata?.layerOrderSlot;
  if (isClickGeometry(slot)) return slot;
  warnUnresolvedLayerOrderSlot(feature, slot);
  return FALLBACK_GEOMETRY;
};

const resolveDeclaredGeometry = (
  declared: DeclaredClickGeometry,
  feature: MapGeoJSONFeature,
): ClickGeometry => {
  if (typeof declared === 'object') {
    return geometryByLayerId(declared.layerIds, feature);
  }
  if (declared === 'fromLayerMetadata') {
    return geometryFromLayerMetadata(feature);
  }
  return declared;
};

/** Geometry a clicked feature contributes, per its type's declaration in `constants.ts`. */
export const getClickGeometry = ({
  entityType,
  feature,
}: FeatureWithEntityType): ClickGeometry => {
  if (!entityType) return FALLBACK_GEOMETRY;

  if (Object.hasOwn(CLICK_GEOMETRY_BY_ROUTER_ENTITY, entityType)) {
    return CLICK_GEOMETRY_BY_ROUTER_ENTITY[entityType as RouterEntityDrawer];
  }

  if (Object.hasOwn(CLICK_GEOMETRY_BY_POI, entityType)) {
    return resolveDeclaredGeometry(
      CLICK_GEOMETRY_BY_POI[entityType as StandardPoiType],
      feature,
    );
  }

  if (Object.hasOwn(CLICK_GEOMETRY_BY_DRAWER_ENTITY_POI, entityType)) {
    return resolveDeclaredGeometry(
      CLICK_GEOMETRY_BY_DRAWER_ENTITY_POI[entityType as DrawerEntityPoiType],
      feature,
    );
  }

  return FALLBACK_GEOMETRY;
};

/** True when the current URL matches a point router entity. */
export const getIsPointRouterEntitySelected = (pathname: string): boolean => {
  const entityType = (
    Object.keys(ROUTER_ENTITY_TO_ROUTE_PATH_MAP) as RouterEntityDrawer[]
  ).find((key) => matchPath(pathname, ROUTER_ENTITY_TO_ROUTE_PATH_MAP[key]));
  return (
    entityType !== undefined &&
    CLICK_GEOMETRY_BY_ROUTER_ENTITY[entityType] === 'point'
  );
};

/** True while moderating a photo submission (the route a photo marker click navigates to). */
export const getIsModeratingPhotoSubmission = (pathname: string): boolean =>
  Boolean(matchPath(pathname, MODERATE_SUBMISSION_ROUTE_PATH));
